<template>
  <div class="language-selector">
    <button 
      @click="toggleDropdown" 
      class="language-button"
      :class="{ 'active': isOpen }"
    >
      <span class="current-language">
        {{ currentLocale.flag }} {{ currentLocale.name }}
      </span>
      <svg 
        class="dropdown-icon" 
        :class="{ 'rotated': isOpen }"
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
      </svg>
    </button>
    
    <div v-if="isOpen" class="language-dropdown">
      <button 
        v-for="locale in availableLocales" 
        :key="locale.code"
        @click="selectLanguage(locale.code)"
        class="language-option"
        :class="{ 'selected': locale.code === currentLanguage }"
      >
        <span class="language-flag">{{ locale.flag }}</span>
        <span class="language-name">{{ locale.name }}</span>
        <svg v-if="locale.code === currentLanguage" class="check-icon" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { availableLocales, setLanguage } from '../locales'

const { locale } = useI18n()
const isOpen = ref(false)

const currentLanguage = computed(() => locale.value)
const currentLocale = computed(() => 
  availableLocales.find(l => l.code === currentLanguage.value) || availableLocales[0]
)

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const selectLanguage = (code: string) => {
  setLanguage(code)
  isOpen.value = false
}

const closeDropdown = (event: Event) => {
  const target = event.target as HTMLElement
  if (!target.closest('.language-selector')) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', closeDropdown)
})

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown)
})
</script>

<style scoped>
.language-selector {
  position: relative;
  z-index: 1000;
}

.language-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.language-button:hover,
.language-button.active {
  background: rgba(255, 255, 255, 0.15);
  border-color: var(--accent-color);
  transform: translateY(-1px);
}

.current-language {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 160px;
  justify-content: flex-start;
}

.dropdown-icon {
  width: 1rem;
  height: 1rem;
  transition: transform 0.3s ease;
}

.dropdown-icon.rotated {
  transform: rotate(180deg);
}

.language-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.5rem;
  background: rgba(30, 27, 75, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  backdrop-filter: blur(20px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: dropdownSlide 0.2s ease-out;
}

@keyframes dropdownSlide {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.language-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.language-option:hover {
  background: rgba(255, 255, 255, 0.1);
}

.language-option.selected {
  background: rgba(59, 130, 246, 0.2);
  color: var(--accent-color);
}

.language-flag {
  font-size: 1.125rem;
  min-width: 1.5rem;
}

.language-name {
  flex: 1;
  text-align: left;
}

.check-icon {
  width: 1rem;
  height: 1rem;
  color: var(--accent-color);
}

@media (max-width: 768px) {
  .current-language {
    min-width: 140px;
  }
  
  .language-button {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
  
  .language-option {
    padding: 0.6rem 0.8rem;
    font-size: 0.8rem;
  }
}
</style>
